import React, { useMemo, useRef } from 'react';
import { useTableColumns } from '../../hooks/useTableColumns';

export interface ColunaTabela<T> {
  titulo: string;
  /** Largura mínima da coluna, em px (entra no `defaultWidths` da medição). */
  largura: number;
  /**
   * Teto da largura mínima, em px — para coluna de texto livre vindo de fora (nome de
   * fornecedor, descrição de peça), que sem isto empurra a tabela para fora do container. A
   * célula precisa truncar com reticências (`render` com `rastreamento-placa-botao` ou estilo de
   * uma linha); o `texto` já vai inteiro no `title`. Ver `useTableColumns.maxWidths`.
   */
  larguraMaxima?: number;
  /** Texto puro da célula — usado na medição das colunas e no `title` do hover. */
  texto: (item: T) => string;
  /** Conteúdo próprio da célula (badge, botão). Sem isto, renderiza o `texto`. */
  render?: (item: T) => React.ReactNode;
}

interface TabelaSistemaProps<T> {
  colunas: ColunaTabela<T>[];
  itens: T[];
  chave: (item: T) => string;
  /** Frase exibida na linha única quando não há itens. */
  vazio: string;
  carregando?: boolean;
  onClickLinha?: (item: T) => void;
}

/**
 * Tabela no padrão ÚNICO das telas do sistema: `TabelaScroll` > `TabelaContent` >
 * `Tabela TabelaHeader` + N linhas `Tabela > Item > Data`, com as colunas medidas pelo
 * hook compartilhado `useTableColumns` (at-43) — o mesmo desenho da Recepção, Chefe de
 * Oficina, Usuários, Tela de Controle e Veículos na Oficina. O CSS vem de
 * `views/recepcao/pages/Recepcao.css`, que a tela hospedeira importa.
 *
 * Existe como componente porque esta tela tem três tabelas (pendências, histórico e as
 * etapas de uma execução): repetir o markup e a fiação do hook três vezes é exatamente o
 * tipo de cópia que o hook da at-43 nasceu para eliminar.
 */
function TabelaSistema<T>({ colunas, itens, chave, vazio, carregando, onClickLinha }: TabelaSistemaProps<T>) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const headerRefs = useRef<(HTMLElement | null)[]>([]);

  const headers = useMemo(() => colunas.map((c) => c.titulo), [colunas]);
  const defaultWidths = useMemo(() => colunas.map((c) => c.largura), [colunas]);
  // `undefined` quando nenhuma coluna tem teto: as outras telas seguem exatamente como antes.
  const maxWidths = useMemo(
    () => (colunas.some((c) => c.larguraMaxima) ? colunas.map((c) => c.larguraMaxima ?? 0) : undefined),
    [colunas],
  );
  const linhasRenderizadas = useMemo(() => itens.map((i) => colunas.map((c) => c.texto(i))), [itens, colunas]);

  const { tableColumns, tableWidth } = useTableColumns({
    containerRef: scrollRef,
    headerCellsRef: headerRefs,
    headers,
    defaultWidths,
    maxWidths,
    rows: linhasRenderizadas,
  });

  const linhaUnica = (texto: string) => (
    <div className="Tabela">
      <div className="Item" style={{ gridColumn: `1 / span ${headers.length}` }}>
        <div className="Data">{texto}</div>
      </div>
    </div>
  );

  return (
    // `minWidth: 0` é o que faz o `overflow-x: auto` do `TabelaScroll` funcionar quando a
    // tabela é filha de um flex column (o corpo do modal): sem isso o item flex usa
    // `min-width: auto`, cresce até a largura do conteúdo e a tabela é cortada pelo
    // `overflow: hidden` do card em vez de rolar. Nas telas de página não muda nada.
    <div className="TabelaScroll" ref={scrollRef} style={{ minWidth: 0 }}>
      <div
        className="TabelaContent"
        style={{ ['--table-columns' as string]: tableColumns, width: tableWidth, minWidth: tableWidth } as React.CSSProperties}
      >
        <div className="Tabela TabelaHeader">
          <div className="Placa">
            <div className="TTulo">
              {headers.map((titulo, i) => (
                <div
                  key={titulo}
                  className="Opcoes"
                  ref={(el) => {
                    headerRefs.current[i] = el;
                  }}
                >
                  {titulo}
                </div>
              ))}
            </div>
          </div>
        </div>

        {carregando && linhaUnica('Carregando...')}
        {!carregando && itens.length === 0 && linhaUnica(vazio)}

        {!carregando &&
          itens.map((item) => (
            <div
              key={chave(item)}
              className="Tabela"
              style={onClickLinha ? { cursor: 'pointer' } : undefined}
              onClick={onClickLinha ? () => onClickLinha(item) : undefined}
            >
              {colunas.map((coluna) => (
                <div className="Item" key={coluna.titulo}>
                  <div className="Data" title={coluna.texto(item)}>
                    {coluna.render ? coluna.render(item) : coluna.texto(item)}
                  </div>
                </div>
              ))}
            </div>
          ))}
      </div>
    </div>
  );
}

export default TabelaSistema;
